import { Show, createEffect, createResource, createSignal, onCleanup, onMount } from "solid-js";
import type { TeacherClass } from "~/features/classes/classes";
import { CommunityTreeCard } from "~/features/today/CommunityTreeCard";
import { GoalsPanel } from "~/features/today/GoalsPanel";
import { TodayOverviewPanel } from "~/features/today/TodayOverviewPanel";
import type { TodayModule } from "~/features/today/today";
import { Icon } from "~/ui/Icon";
import { WeekBanner } from "./WeekBanner";

function className(value: TeacherClass) {
  const school = value.teacher_school_name || value.school_name;
  return school ? `${value.name} · ${school}` : value.name;
}

function greeting() {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 11) return "Guten Morgen";
  if (hour >= 11 && hour < 14) return "Guten Mittag";
  if (hour >= 14 && hour < 18) return "Guten Nachmittag";
  if (hour >= 18 && hour < 22) return "Guten Abend";
  return "Gute Nacht";
}

function greetingIcon(): "sun" | "cloudSun" | "moon" | "star" {
  const hour = new Date().getHours();
  return hour >= 5 && hour < 12 ? "sun" : hour >= 12 && hour < 18 ? "cloudSun" : hour >= 18 && hour < 22 ? "moon" : "star";
}

export function TeacherTodayPage(props: { value?: TeacherClass; module: TodayModule; onNavigateVoting: () => void }) {
  const [snapshot, { refetch }] = createResource(() => props.value?.id, (classId) => props.module.load(classId));
  const [lastUpdated, setLastUpdated] = createSignal(new Date());
  const [error, setError] = createSignal("");
  const today = () => new Date().toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" });
  const isPausedWeekend = () => {
    const day = new Date().getDay();
    const config = snapshot()?.weekInfo?.weekend_config ?? {};
    return (day === 6 && !config.saturday) || (day === 0 && !config.sunday);
  };
  const reload = async () => {
    setError("");
    try { await refetch(); setLastUpdated(new Date()); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "Tagesübersicht konnte nicht geladen werden"); }
  };

  createEffect(() => { if (snapshot()) setLastUpdated(new Date()); });
  onMount(() => {
    const interval = window.setInterval(() => void reload(), 60_000);
    onCleanup(() => window.clearInterval(interval));
  });

  return (
    <Show when={props.value} fallback={<div class="card" style={{ "text-align": "center", padding: "40px" }}><p>Bitte wähle zuerst eine Klasse aus.</p></div>}>
      {(value) => (
        <>
          <div style={{ "margin-bottom": "16px" }}><h1>{className(value())}</h1><p style={{ color: "var(--text-muted)" }}>{today()}</p></div>
          <WeekBanner value={snapshot()?.weekInfo ?? undefined} />
          <Show when={isPausedWeekend()}>
            <div class="card" style={{ background: "#C4B5FD", border: "2.5px solid #6D28D9", display: "flex", "align-items": "center", gap: "16px", padding: "18px 22px", "margin-bottom": "16px" }}>
              <Icon name="cloudSun" style={{ width: "32px", height: "32px", "flex-shrink": 0 }} />
              <div><div style={{ "font-size": "1rem", "font-weight": 900, color: "#3730a3", "margin-bottom": "4px" }}>Heute ist kein Schultag</div><div style={{ "font-size": "0.88rem", "font-weight": 600, color: "#4338ca", "line-height": 1.5 }}>Auch du kannst Pause machen! Check-ins laufen am Wochenende nicht — die Klasse startet frisch am Montag.</div></div>
            </div>
          </Show>
          <Show when={snapshot()} fallback={<Show when={snapshot.error} fallback={<div style={{ display: "flex", "justify-content": "center", padding: "40px" }}><div class="loading-spinner" /></div>}><div class="card"><p>Tagesübersicht konnte nicht geladen werden.</p><button class="btn btn-primary" type="button" onClick={() => void reload()} style={{ "margin-top": "12px" }}>Erneut versuchen</button></div></Show>}>
            {(data) => (
              <>
                <div class="card" style={{ background: "var(--bg-card)", border: "2.5px solid var(--secondary)", display: "flex", "align-items": "center", gap: "14px", padding: "14px 18px", "margin-bottom": "16px" }}>
                  <Icon name={greetingIcon()} style={{ width: "16px", height: "16px" }} />
                  <span style={{ "font-size": "1rem", "font-weight": 700, color: "var(--ink)", flex: 1 }}>{greeting()}!</span>
                  <span style={{ "font-size": "0.85rem", "font-weight": 600, color: "var(--ink)" }}>Stand: {lastUpdated().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} · alle 60 s</span>
                  <button type="button" onClick={() => void reload()} title="Jetzt aktualisieren" style={{ background: "transparent", border: "none", cursor: "pointer", padding: "4px 6px", "border-radius": "6px" }}><Icon name="refresh" style={{ width: "16px", height: "16px" }} /></button>
                </div>
                <Show when={error()}><div class="form-error" role="alert" style={{ "margin-bottom": "16px" }}>{error()}</div></Show>
                <GoalsPanel classId={value().id} overview={data().overview} goal={data().classGoal} goalResults={data().classGoalResults} yesterday={data().yesterday} onVoting={props.onNavigateVoting} onSaveGoal={async (text) => { await props.module.saveClassGoal(value().id, text); await reload(); }} onResetGoal={async () => { await props.module.resetClassGoal(value().id); await reload(); }} onResetVoting={async () => { await props.module.resetVoting(value().id); await reload(); }} />
                <div style={{ display: "grid", "grid-template-columns": "repeat(auto-fit,minmax(min(100%,340px),1fr))", gap: "16px", "align-items": "start" }}>
                  <div style={{ "min-width": 0 }}><TodayOverviewPanel value={data().overview} onResetStudent={async (studentId) => { await props.module.resetStudentToday(value().id, studentId); await reload(); }} /></div>
                  <CommunityTreeCard value={data().tree} />
                </div>
              </>
            )}
          </Show>
        </>
      )}
    </Show>
  );
}
